import { ComplianceRequirement } from 'types/compliance';

/**
 * ListingAccessPolicy
 * 
 * Domain policy that determines if listing content requires user action
 * (sign-in or terms acceptance) before full details can be shown.
 * 
 * This is separate from MLS compliance (legal text requirements).
 * 
 * - **Access Policy** (this): Does user need to sign in / accept terms?
 * - **MLS Compliance** (MlsCompliancePolicy): Legal text we must display
 * 
 * Components/sections decide how to handle restricted content:
 * - Gallery: blur images
 * - Price: blur amount, show currency symbol
 * - Stats: blur values, show icons
 * - Recency: hide completely
 * 
 * @example
 * ```typescript
 * const access = ListingAccessPolicy.from(complianceRequirement);
 * 
 * // Components decide their own behavior
 * <ListingPrice blur={access.hasAccessRestriction} />
 * <ListingStats blur={access.hasAccessRestriction} />
 * {!access.hasAccessRestriction && <ListingRecency />}
 * ```
 */
export class ListingAccessPolicy {
  private constructor(private readonly requirement: ComplianceRequirement) {}

  /**
   * Creates a policy from a compliance requirement.
   */
  static from(requirement: ComplianceRequirement): ListingAccessPolicy {
    return new ListingAccessPolicy(requirement);
  }

  /**
   * Whether access to full listing details is restricted.
   * 
   * True when user needs to sign in or accept terms of use.
   * Components/sections use this to decide their own visibility behavior.
   */
  get hasAccessRestriction(): boolean {
    return this.requirement !== ComplianceRequirement.NONE;
  }

  /**
   * The specific action required (for overlay messaging).
   */
  get actionRequired(): ComplianceRequirement {
    return this.requirement;
  }
}
